import { Avatar, Link } from "@mui/material";
import {
  FC,
  useState,
  MouseEvent,
  ChangeEvent,
  useEffect,
  useMemo,
} from "react";
import { getUsers } from "../../api/users/user.api";
import {
  IUserColumns,
  IUserData,
} from "../../common/interfaces/user.interface";
import { DataTable } from "../../components/tables/DataTable";
import useFuse from "../../hooks/useFuse";
import clubAvatar from "../../assets/clubAvatar.png";
import { useNavigate } from "react-router";
import { USERS } from "../../common/constants/routes.constants";
import { getTrimmedPublicKey } from "../../utils/helpers";
import WhitelistingStatusChip from "../../components/WhitelistingStatus/WhitelistingStatus";
import {
  MonkeAdminPermissions,
  MonkeAdminStatus,
  WhitelistingStatus,
} from "../../common/enums/whitelisting.enum";
import { useAnchorWallet } from "@solana/wallet-adapter-react";
import {
  getMonkeAdmins,
  getWhitelistings,
} from "../../solana/methods/whitelisting";
import { PublicKey } from "@solana/web3.js";
import FilterItem from "../../components/FilterItem/FilterItem";
import AddBulkMembersModal from "../../components/AddBulkMembersModal/AddBulkMembersModal";

const Users: FC = () => {
  const [page, setPage] = useState<number>(0);
  const [items, setItems] = useState<IUserData[]>([]);
  const [search, setSearch] = useState<string>("");
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const navigate = useNavigate();
  const wallet = useAnchorWallet();
  const [whitelistedUsers, setWhitelistedUsers] = useState<PublicKey[]>([]);
  const [filteredItems, setFilteredItems] = useState<IUserData[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<WhitelistingStatus[]>(
    []
  );
  const [hasPermission, toggleHasPermission] = useState(false);
  const [isModalVisible, toggleIsModalVisible] = useState(false);

  useEffect(() => {
    void getUsersTableInfo();
  }, [wallet]);

  useEffect(() => {
    if (appliedFilters.length === 0) {
      setFilteredItems(items);
    } else {
      setFilteredItems([
        ...items.filter((item) =>
          appliedFilters.includes(item.whitelisting.status)
        ),
      ]);
    }
  }, [appliedFilters]);

  const getUsersTableInfo = async () => {
    try {
      const users = await getUsers();

      if (wallet) {
        const whitelistings = await getWhitelistings(wallet);
        const admins = await getMonkeAdmins(wallet);
        toggleHasPermission(
          !!admins.find(
            (config) =>
              config.admin.toString() === wallet.publicKey.toString() &&
              !!config.permissions.find(
                (perm) =>
                  Object.keys(perm)[0] === MonkeAdminPermissions.AddWhitelisting
              ) &&
              Object.keys(config.status)[0] === MonkeAdminStatus.Accepted
          )
        );
        const items = [
          ...users.map((user) => {
            const isWhitelisted = !!whitelistings.find(
              (whitelisting) => whitelisting.toString() === user.pubKey
            );
            return {
              ...user,
              whitelisting:
                user.whitelisting || isWhitelisted
                  ? {
                      ...user.whitelisting,
                      status: isWhitelisted
                        ? WhitelistingStatus.Approved
                        : user.whitelisting.status,
                    }
                  : { status: WhitelistingStatus.Denied },
            };
          }),
        ];
        setItems(items);
        setFilteredItems(items);
        setWhitelistedUsers(whitelistings);
      } else {
        setItems(users);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handlePageChange = (
    event: MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const onRowsPerPageChange = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const goToUserDetails = (value) => {
    navigate(`${USERS}/${value.pubKey}`);
  };

  const userTableColumns: IUserColumns[] = useMemo(() => {
    return [
      {
        id: "imageUrl",
        label: "Avatar",
        minWidth: 100,
        format: (value) => {
          return (
            <Avatar
              src={value === "" ? clubAvatar : value}
              alt="User avatar"
              style={{ backgroundColor: "transparent" }}
            />
          );
        },
      },
      {
        id: "user",
        label: "Username",
        minWidth: 170,
        format: (value: IUserData) => {
          return (
            <div>
              <p>{value.userName ? value.userName : "N/A"}</p>
              <p>
                {value.whitelisting.discordSnowflake ? (
                  <>
                    <p
                      style={{
                        fontSize: "10px",
                        color: "#2bfda7",
                      }}
                    >
                      {value.whitelisting.discordSnowflake}
                    </p>
                    <p>{value.discordContact ? value.discordContact : ""}</p>
                  </>
                ) : null}
              </p>
            </div>
          );
        },
      },
      {
        id: "pubKey",
        label: "Address",
        minWidth: 170,
        format: (value) => {
          return <Link>{getTrimmedPublicKey(value)}</Link>;
        },
      },
      {
        id: "createdAt",
        label: "Created at",
        minWidth: 150,
        format: (value) => {
          return new Date(value).toDateString();
        },
      },
      {
        id: "user",
        label: "Whitelisting status",
        minWidth: 100,
        format: (value: IUserData) => {
          return (
            <div
              style={{
                width: "6em",
                marginLeft: "1.5em",
                display: "flex",
                alignItems: "center",
                gap: "5em",
              }}
            >
              <WhitelistingStatusChip user={value} />
            </div>
          );
        },
      },
    ];
  }, [wallet]);

  const options = {
    includeScore: true,
    keys: ["pubKey", "discord", "userName", "whitelisting.discordSnowflake"],
    threshold: 0.2,
  };

  const filteredList: IUserData[] = useFuse(filteredItems, search, options);

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const applyFilters = (status: WhitelistingStatus) => {
    if (appliedFilters.includes(status)) {
      setAppliedFilters([...appliedFilters.filter((st) => st !== status)]);
    } else {
      setAppliedFilters([...appliedFilters, status]);
    }
  };

  return (
    <div>
      <div>
        {isModalVisible && (
          <AddBulkMembersModal closeModal={() => toggleIsModalVisible(false)} />
        )}
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div style={{ display: "flex", gap: "1em" }}>
            <h2>Users page</h2>
            <input
              type={"text"}
              style={{
                borderRadius: "8px",
                border: "1px solid white",
                background: "transparent",
                padding: "0.5em",
                width: "20em",
                color: "white",
              }}
              placeholder="Search"
              onChange={(e) => setSearch(e.target.value)}
            />
            <FilterItem
              title="Approved"
              handleFilter={() => applyFilters(WhitelistingStatus.Approved)}
              isActive={appliedFilters.includes(WhitelistingStatus.Approved)}
            />
            <FilterItem
              handleFilter={() => applyFilters(WhitelistingStatus.Pending)}
              title="Pending"
              isActive={appliedFilters.includes(WhitelistingStatus.Pending)}
            />
            <FilterItem
              title="Denied"
              handleFilter={() => applyFilters(WhitelistingStatus.Denied)}
              isActive={appliedFilters.includes(WhitelistingStatus.Denied)}
            />
          </div>
          {hasPermission && (
            <button
              onClick={() => toggleIsModalVisible(true)}
              style={{
                color: "white",
                background: "transparent",
                border: "2px solid white",
                borderRadius: "4px",
                fontWeight: "700",
                padding: "0.5em",
                cursor: "pointer",
              }}
            >
              Add Whitelistings
            </button>
          )}
        </div>

        <DataTable
          page={page}
          rowsPerPage={rowsPerPage}
          items={filteredList}
          columns={userTableColumns}
          itemsCount={items.length}
          onPageChange={handlePageChange}
          onSearch={handleSearch}
          onRowsPerPageChange={onRowsPerPageChange}
          onRowClick={goToUserDetails}
          hasPermission={hasPermission}
        />
      </div>
    </div>
  );
};

export default Users;
